import React, { useRef, useState, useEffect } from "react";
import { Save } from "lucide-react";
import { getProfile, BASE_URL, uploadProfilePhoto } from "../api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
export default function ProfilePage({ onBack }) {
  const fileRef = useRef();
  const [form, setForm] = useState({ firstName: "", lastName: "", phone: "", department: "", role: "", status: "" });

  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["profile"],
    queryFn: getProfile,
    staleTime: 5 * 60 * 1000,
  });
  const user = data?.user ?? {};
  const photoUrl = user.profilePhoto
    ? user.profilePhoto.startsWith("http") ? user.profilePhoto : `${BASE_URL}${user.profilePhoto}`
    : null;
  const [localPhoto, setLocalPhoto] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);

  useEffect(() => {
    if (data?.user) {
      const u = data.user;
      setForm({ firstName: u.firstName || "", lastName: u.lastName || "", phone: u.phone || "", department: u.department || "", role: u.role || "", status: u.status || "" });
    }
  }, [data]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const saveMutation = useMutation({
    mutationFn: async () => {
      const formData = new FormData();
      formData.append("firstName", form.firstName);
      formData.append("lastName", form.lastName);
      formData.append("phone", form.phone);
      if (photoFile) formData.append("photo", photoFile);
      const res = await fetch(`${BASE_URL}/api/profile`, {
        method: "PATCH",
        credentials: "include",
        body: formData,
      });
      return res.json();
    },
    onSuccess: (res) => {
      if (res.user?.profilePhoto) {
        const p = res.user.profilePhoto;
        setLocalPhoto(p.startsWith("http") ? p : `${BASE_URL}${p}`);
      }
      qc.invalidateQueries({ queryKey: ["profile"] });
    },
  });

  return (
    <div style={s.container}>
      <h2 style={s.title}>Profile</h2>
      <p style={s.subtitle}>Manage your profile or settings</p>

      {isLoading ? (
        <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Loading...</div>
      ) : (
      <div style={s.card}>
        <div style={s.avatarRow}>
          <div style={s.avatarBox} onClick={() => fileRef.current.click()}>
            {(localPhoto || photoUrl)
              ? <img src={localPhoto || photoUrl} alt="avatar" style={s.avatarImg} onError={(e) => { e.target.style.display = "none"; }}/>
              : <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
            }
          </div>
          <div>
            <p style={s.uploadTitle}>{user.firstName} {user.lastName}</p>
            <p style={s.uploadSub}>{user.employeeId}</p>
          </div>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
            onChange={(e) => { const f = e.target.files[0]; if (f) { setLocalPhoto(URL.createObjectURL(f)); setPhotoFile(f); } }} />
        </div>

        <div style={s.grid2}>
          <div style={s.field}>
            <label style={s.label}>First Name</label>
            <input style={s.input} value={form.firstName} onChange={set("firstName")} />
          </div>
          <div style={s.field}>
            <label style={s.label}>Last Name</label>
            <input style={s.input} value={form.lastName} onChange={set("lastName")} />
          </div>
        </div>

        <div style={s.grid2}>
          <div style={s.field}>
            <label style={s.label}>Email</label>
            <input style={{ ...s.input, background: "#f8fafc", color: "#94a3b8" }} value={user.email || ""} readOnly />
          </div>
          <div style={s.field}>
            <label style={s.label}>Phone Number</label>
            <input style={s.input} placeholder="Enter phone number" value={form.phone} onChange={set("phone")} />
          </div>
        </div>

        <div style={s.grid2}>
          <div style={s.field}>
            <label style={s.label}>Department</label>
            <input style={{ ...s.input, background: "#f8fafc", color: "#94a3b8" }} value={form.department} readOnly />
          </div>
          <div style={s.field}>
            <label style={s.label}>Employee ID</label>
            <input style={{ ...s.input, background: "#f8fafc", color: "#94a3b8" }} value={user.employeeId || ""} readOnly />
          </div>
        </div>

        <div style={s.grid2}>
          <div style={s.field}>
            <label style={s.label}>Role</label>
            <input style={{ ...s.input, background: "#f8fafc", color: "#94a3b8" }} value={form.role} readOnly />
          </div>
          <div style={s.field}>
            <label style={s.label}>Status</label>
            <input style={{ ...s.input, background: "#f8fafc", color: form.status === "active" ? "#16a34a" : "#dc2626", fontWeight: 600 }} value={form.status} readOnly />
          </div>
        </div>

        <div style={s.grid2}>
          <div style={s.field}>
            <label style={s.label}>Last Login</label>
            <input style={{ ...s.input, background: "#f8fafc", color: "#94a3b8" }} value={user.lastLogin ? new Date(user.lastLogin).toLocaleString() : "N/A"} readOnly />
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
          <button style={s.saveBtn} onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
            <Save size={14} /> {saveMutation.isPending ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
      )}
    </div>
  );
}

const s = {
  container:    { padding: 24, background: "#f8fafc", minHeight: "100vh" },
  title:        { fontSize: 20, fontWeight: 700, color: "#1e293b", marginBottom: 2 },
  subtitle:     { fontSize: 13, color: "#64748b", marginBottom: 20 },
  card:         { background: "#fff", borderRadius: 12, padding: 28, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" },
  avatarRow:    { display: "flex", alignItems: "center", gap: 16, marginBottom: 24, paddingBottom: 20, borderBottom: "1px solid #f1f5f9" },
  avatarBox:    { width: 70, height: 70, borderRadius: "50%", border: "2px dashed #cbd5e1", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", overflow: "hidden", flexShrink: 0 },
  avatarImg:    { width: "100%", height: "100%", objectFit: "cover" },
  uploadTitle:  { fontSize: 14, fontWeight: 600, color: "#080e21", marginBottom: 2 },
  uploadSub:    { fontSize: 12, color: "#94a3b8" },
  grid2:        { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 },
  field:        { display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 },
  label:        { fontSize: 13, fontWeight: 500, color: "#374151" },
  input:        { padding: "9px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, color: "#475569", outline: "none", background: "#fff" },
  select:       { padding: "9px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, color: "#475569", outline: "none", background: "#fff", cursor: "pointer" },
  saveBtn:      { display: "flex", alignItems: "center", gap: 6, padding: "9px 18px", background: "#080e21", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13 },
};
